import {
  observable,
  reaction,
} from 'mobx';
import { RemoteDisplayBatchStatus } from '../constants/RemoteDisplayBatchStatus';
import { DeviceTypes } from '../constants/DeviceModels';
import { ProxyCommands } from '../constants/ProxyCommands';
import logger from '../utils/logger';

class RemoteDisplayStore {
  @observable
    ports = [];
  @observable
    rds = new Map();

  constructor(rootStore) {
    this.rootStore = rootStore;
    const { batchStore } = this.rootStore;

    this.connect();

    reaction(
      () => !batchStore.isDemo,
      () => {
        this.connect();
      }
    );
  }

  connect() {
    const { asyncStore, batchStore, pacStore, devicesStore } = this.rootStore;
    const remoteDisplayDevices = devicesStore.devices.filter(v => v.deviceTypeName === DeviceTypes.RemoteDisplay && v.portCOM);

    asyncStore.clearTimeout(batchStore.rdConnectTimer);

    if (!batchStore.proxyVersion && !batchStore.isDemo) {
      if (pacStore.isWSOpen() && remoteDisplayDevices.length) {
        pacStore.runWS(ProxyCommands.GET_PROXY_VERSION);
      }

      batchStore.rdConnectTimer = asyncStore.setTimeout(() => {
        this.connect();
      }, 1000);
      return;
    }

    if (!this.ports.length && !batchStore.isDemo) {
      if (pacStore.isWSOpen() && remoteDisplayDevices.length) {
        pacStore.runWS(ProxyCommands.RD_GET_PORTS);
      }

      batchStore.rdConnectTimer = asyncStore.setTimeout(() => {
        this.connect();
      }, 1000);

      return;
    }

    batchStore.rdConnectTimer = asyncStore.setTimeout(() => {
      remoteDisplayDevices.forEach(device => {
        this.setStatus(device.id);

        if (!batchStore.isDemo) {
          if (!this.ports.find(v => v.path === `COM${device.portCOM}`)) {
            logger.log(`Port COM${device.portCOM} unavailable!`);
            return;
          }

          const rd = {
            path: device.portCOM ? `COM${device.portCOM}` : '', // 'COM3'
            baudRate: device.baudRate || 9600,
            dataBits: device.dataBits || 8,
            stopBits: device.stopBits || 1,
            parity: device.parity || 'none',
          };

          logger.log(ProxyCommands.RD_CONNECT, rd.path, rd, device);
          pacStore.runWS(`${ProxyCommands.RD_CONNECT} ${JSON.stringify(rd)}`);
        }
      });

      this.startStream();
    }, 1000);
  }

  /**
   * Get command
   *
   * str: <STX><LIGHT:P><TEXT:N=%><ETX>
   * LIGHT:
   *   P red & green lights
   *   @ green light
   *   0 red light
   *     no lights (empty space)
   * TEXT:
   *   N means the name of material
   *   T means target weight
   *   B means balance weight
   *   W means scale reading weight
   *
   * @param str
   * @param rd
   * @returns {string}
   */
  getCmd(str, rd) {
    let parts = String(str).split(/<|>/);
    let light = parts.find(v => v.includes('LIGHT:'));
    light = light ? light.replace('LIGHT:', '') : ' ';
    let text = parts.find(v => v.includes('TEXT:'));
    text = text ? text.replace('TEXT:', '') : '';

    if (text) {
      if (text.indexOf('N=') === 0) {
        text = rd.material;
      } else if (text.indexOf('T=') === 0) {
        text = rd.target;
      } else if (text.indexOf('B=') === 0) {
        text = rd.balance;
      } else if (text.indexOf('W=') === 0) {
        text = rd.currentWeight;
      }
    }

    text = String(text);
    const size = text.length;

    if (size < 6) {
      text = ' '.repeat(6 - size) + text;
    }

    return ` ${light} ${text}`;
  }

  getStatusFromCmd(cmd) {
    const light = cmd.charAt(1);
    let status = {
      isGreenLightOn: false,
      isRedLightOn: false,
      textToDisplay: cmd.replace(light, ''),
    };

    if (light === 'P') {
      status.isRedLightOn = true;
      status.isGreenLightOn = true;
    } else if (light === '0') {
      status.isRedLightOn = true;
    } else if (light === '@') {
      status.isGreenLightOn = true;
    }

    return status;
  }

  getUIStatus(id) {
    const s = this.getStatus(id);
    return this.getStatusFromCmd(s.cmd);
  }

  getStatus(id) {
    const { devicesStore } = this.rootStore;
    const d = devicesStore.getDeviceById(id) || {};
    const s = this.rds.get(id) || {};

    return {
      id,
      path: d.portCOM ? `COM${d.portCOM}` : '',
      promptOne: d.promptOne || '',
      promptTwo: d.promptTwo || '',
      weight: d.weight || '',
      discharge: d.discharge || '',
      idle: d.idle || '',
      streamInterval: d.streamInterval || 1,
      promptInterval: d.promptSwitchInterval || 2,
      status: s.status || RemoteDisplayBatchStatus.IDLE,
      material: s.material || '',
      target: s.target || 0,
      balance: s.balance || 0,
      currentWeight: s.currentWeight || 0,
      cmd: s.cmd || '',
    };
  }

  setStatus(id, options = {}) {
    const s = this.getStatus(id) || {};
    this.rds.set(id, {
      status: options.status || s.status || RemoteDisplayBatchStatus.IDLE,
      material: options.material || s.material || '',
      target: options.target || s.target || 0,
      balance: options.balance || s.balance || 0,
      currentWeight: options.currentWeight || s.currentWeight || 0,
      cmd: options.cmd || s.cmd || '',
    });
  }

  clearStatuses() {
    Array.from(this.rds.keys()).forEach(id => {
      this.setStatus(id, {
        status: RemoteDisplayBatchStatus.IDLE,
        material: ' ',
        target: '0',
        balance: '0',
        currentWeight: '0',
      });
    });
  }

  savePorts(ports) {
    this.ports = ports;
  }

  onConnectFail(path) {
    this.ports.filter(v => v.path !== path);
  }

  startStream() {
    Array.from(this.rds.keys()).forEach(id => {
      this.streamInterval(id);
    });
  }

  checkDevicesExist() {
    const { devicesStore } = this.rootStore;
    const rds = devicesStore.devices.filter(v => v.deviceTypeName === DeviceTypes.RemoteDisplay && v.portCOM);

    if (!rds.length) {
      this.ports = [];
      this.rds = new Map();
    }
  }

  streamInterval(id, counter = 1) {
    const { asyncStore, batchStore, pacStore } = this.rootStore;
    const rd = this.getStatus(id);
    this.checkDevicesExist();

    if (!this.ports.length && !batchStore.isDemo) {
      this.connect();
      return;
    }

    asyncStore.clearTimeout(batchStore[`rd-timer-${rd.id}`]);
    const delay = rd.status === RemoteDisplayBatchStatus.START_WEIGHT
      ? rd.promptInterval
      : rd.streamInterval;

    batchStore[`rd-timer-${rd.id}`] = asyncStore.setTimeout(() => {

      let cmd = '';

      switch (rd.status) {
        case RemoteDisplayBatchStatus.IDLE:
          cmd += this.getCmd(rd.idle, rd);
          break;
        case RemoteDisplayBatchStatus.START_WEIGHT:
          cmd += this.getCmd(counter % 2 ? rd.promptOne : rd.promptTwo, rd);
          break;
        case RemoteDisplayBatchStatus.WEIGHT:
          cmd += this.getCmd(rd.weight, rd);
          break;
        case RemoteDisplayBatchStatus.DISCHARGE:
          cmd += this.getCmd(rd.discharge, rd);
          break;
        case RemoteDisplayBatchStatus.FINISH:
          cmd += this.getCmd(rd.idle, rd);
          break;
        default:
          cmd += '   --//--';
          logger.log('rd: unknown status', rd.status, rd);
      }

      logger.log(rd.status, `${ProxyCommands.RD_DISPLAY} ${rd.path} ${cmd}`, rd);

      this.setStatus(rd.id, { cmd });

      if (pacStore.isWSOpen() && batchStore.canEmit && this.ports.find(v => v.path === rd.path)) {
        pacStore.runWS(`${ProxyCommands.RD_DISPLAY} ${rd.path} ${batchStore.isDemo && counter % 2 ? '    DEMO ' : cmd}`);
      }

      this.streamInterval(id, counter+1);

    }, delay * 1000);
  }
}

export default RemoteDisplayStore;
